import { createContext, useMemo } from 'react';
import { isEqual } from 'lodash';
import type { ResultOf } from '@graphql-typed-document-node/core';
import type { UseQueryResult } from '@tanstack/react-query';
import type { JSX } from 'react';

import { getFragmentData, graphql } from '@e-socna/data-layer';
import { EMPTY_ARRAY, EMPTY_STRING } from '@e-socna/empty';
import { EMPTY_ID, EMPTY_ID_ARRAY } from '@e-socna/empty-client';

import { useMemoCompare } from '../../hooks/useMemoCompare.hook';
import { useGraphQLBasicQuery } from '../../hooks/useGraphQLBasicQuery.hook';

const emptyUser = {
  _id: EMPTY_ID,
  email: EMPTY_STRING,
  globalRoleIds: EMPTY_ID_ARRAY,
  permissionCodes: EMPTY_ARRAY,
  lastSeenAt: EMPTY_STRING,
  uiSettings: {
    paletteModeId: EMPTY_ID,
    themeMainColor: null,
    isContextualColorEnabled: true,
  },
  mustChangePassword: false,
  person: {
    _id: EMPTY_ID,
    allMemberships: EMPTY_ARRAY,
    civilityId: EMPTY_ID,
    firstName: EMPTY_STRING,
    lastName: EMPTY_STRING,
    pictureImageUri: EMPTY_STRING,
  },
} as const;

export const AuthenticationContextProvider_AuthenticatedUser_Fragment = graphql(
  `
    fragment AuthenticationContextProvider_AuthenticatedUser_Fragment on AuthenticatedUser {
      _id
      email
      lastSeenAt
      uiSettings {
        paletteModeId
        themeMainColor
        isContextualColorEnabled
      }
      globalRoleIds
      permissionCodes
      mustChangePassword
      person {
        _id
        allMemberships {
          _id
          organizationId
          contextualRoleIds
          statusId
        }
        civilityId
        firstName
        lastName
        pictureImageUri
      }
    }
  `,
);

export type AuthenticationContextProvider_AuthenticatedUser = ResultOf<
  typeof AuthenticationContextProvider_AuthenticatedUser_Fragment
>;

export const AuthenticationContextProvider_AuthenticatedUser_QueryDocument =
  graphql(`
    query AuthenticationContextProvider_AuthenticatedUser_Query {
      getAuthenticated {
        ...AuthenticationContextProvider_AuthenticatedUser_Fragment
      }
    }
  `);

interface AuthenticationData {
  readonly authenticationQuery: UseQueryResult<
    ResultOf<
      typeof AuthenticationContextProvider_AuthenticatedUser_QueryDocument
    >
  >;
  readonly authenticationData:
    | AuthenticationContextProvider_AuthenticatedUser
    | typeof emptyUser
    | null
    | undefined;
}

const AuthenticationContext = createContext<AuthenticationData | null>(null);

interface Props {
  readonly children: JSX.Element;
}

const AuthenticationContextProvider = ({
  children,
}: Readonly<Props>): JSX.Element => {
  const authenticationQuery = useGraphQLBasicQuery(
    AuthenticationContextProvider_AuthenticatedUser_QueryDocument,
  );

  const context = {
    authenticationQuery,
    authenticationData:
      authenticationQuery.isSuccess &&
      !('mustLogout' in authenticationQuery.data)
        ? getFragmentData(
            AuthenticationContextProvider_AuthenticatedUser_Fragment,
            authenticationQuery.data.getAuthenticated,
          )
        : emptyUser,
  };

  const memoizedContext = useMemoCompare(context, isEqual);

  const authenticationContext = useMemo(
    () => memoizedContext,
    [memoizedContext],
  );

  return (
    <AuthenticationContext.Provider value={authenticationContext}>
      {children}
    </AuthenticationContext.Provider>
  );
};

export { AuthenticationContext, AuthenticationContextProvider };
